/**
 * Consumer limits: the rules the console applies before it sends anything.
 *
 * Deposits, withdrawals and P2P transfers each have a weekly and a monthly
 * ceiling, in USD. Weeks start Monday 00:00 Kigali time and months are
 * calendar months. A consumer at a limit is refused; support or compliance
 * can raise one user's limits with an override that carries a reason and an
 * optional expiry.
 *
 * Everything here is pure, with no imports, for two reasons. The forms in
 * `components/limits` and `components/users` share it, so a rule cannot be
 * enforced on one screen and forgotten on the other. And `node --test` can
 * load it directly (`tests/consumerLimits.test.mjs`) without a bundler.
 *
 * The console refuses what the API would refuse, so an operator sees the
 * problem next to the field rather than as a 422 after the request. It is not
 * the control. The API checks every one of these again.
 */

export const CURRENCY = 'USD';

/**
 * The contract's paths, in one place so the service and the testing-mode
 * mock cannot drift apart, and so a test can check them against the contract.
 */
export const CONSUMER_DEFAULTS_PATH = '/api/v1/admin/limits/consumer-defaults';

export function userLimitsPath(userId) {
  return `/api/v1/admin/users/${encodeURIComponent(userId)}/limits`;
}

/** Order matters: it is the order the rows appear in on both screens. */
export const DIRECTIONS = [
  {
    key: 'deposit',
    label: 'Deposits',
    description: 'Money coming into the wallet, such as top-ups.',
  },
  {
    key: 'withdrawal',
    label: 'Withdrawals',
    description:
      'Money leaving the wallet: payouts, bill payments and merchant payments, counted together.',
  },
  {
    key: 'p2p',
    label: 'P2P transfers',
    description: 'Wallet-to-wallet transfers between Inverse users.',
  },
];

export const WINDOWS = [
  { key: 'weekly', label: 'Weekly', hint: 'Monday 00:00 to Sunday 23:59, Kigali time' },
  { key: 'monthly', label: 'Monthly', hint: 'Calendar month, Kigali time' },
];

/**
 * Per-user values above this need a Super Admin. The API answers 403
 * otherwise, so the console says so at the field instead.
 */
export const SUPER_ADMIN_OVERRIDE_CEILING_USD = 50000;

/**
 * The floor the console's other reason dialogs use (`ConfirmWithReason`).
 * The contract only says the reason is required. Ten characters is kept here
 * as well so the screens agree: these reasons are read later by whoever is
 * reconstructing why a customer could move more money than the default, and
 * "ok" tells them nothing.
 */
export const MIN_REASON_LENGTH = 10;

/** Kigali is UTC+2 all year. There is no daylight saving to account for. */
const KIGALI_OFFSET_MS = 2 * 60 * 60 * 1000;

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

/**
 * "3,750" and "3750.50" become numbers. Anything else becomes NaN.
 *
 * Commas and spaces are accepted because operators type amounts the way they
 * read them. More than two decimals is refused rather than rounded, because
 * a rounding the operator did not see is a limit they did not set.
 */
export function parseAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (typeof value !== 'string') return NaN;
  const cleaned = value.replace(/[,\s]/g, '');
  if (!AMOUNT_PATTERN.test(cleaned)) return NaN;
  return Number(cleaned);
}

/** Display only. Never parsed back. */
export function formatUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const value = Number(amount);
  const hasCents = Math.round(value * 100) % 100 !== 0;
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })} ${CURRENCY}`;
}

/**
 * An instant as Kigali wall-clock time, e.g. "Mon 20 Oct 2026, 00:00".
 *
 * Always Kigali, never the browser's zone. The windows are defined in Kigali
 * time, and an operator in another zone reading "resets Sunday 23:00" would
 * reasonably conclude the week ends on a Sunday.
 */
export function formatKigaliDateTime(iso, { withTime = true } = {}) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    timeZone: 'Africa/Kigali',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
}

/** The amount as an input shows it: no grouping, so it parses back cleanly. */
function toInputValue(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  return String(Number(amount));
}

/**
 * Check one direction's weekly and monthly values.
 *
 * Returns `{ weekly?, monthly? }` messages; an empty object means valid.
 * `ceiling` is the per-user Super Admin threshold, passed only when the
 * acting admin is not a Super Admin.
 */
export function validateWindowPair({ weekly, monthly }, { ceiling } = {}) {
  const errors = {};
  const values = { weekly: parseAmount(weekly), monthly: parseAmount(monthly) };

  for (const { key } of WINDOWS) {
    const raw = key === 'weekly' ? weekly : monthly;
    const value = values[key];
    if (raw == null || String(raw).trim() === '') {
      errors[key] = 'Enter an amount.';
    } else if (Number.isNaN(value)) {
      errors[key] = 'Enter a number, such as 3750 or 3750.50.';
    } else if (value <= 0) {
      errors[key] = 'Must be more than 0.';
    } else if (ceiling != null && value > ceiling) {
      errors[key] = `Above ${formatUsd(ceiling)} needs a Super Admin.`;
    }
  }

  if (!errors.weekly && !errors.monthly && values.monthly < values.weekly) {
    errors.monthly = `Can't be less than the weekly limit (${formatUsd(values.weekly)}).`;
  }

  return errors;
}

/* --- Defaults ------------------------------------------------------------ */

/**
 * API shape to form state. A direction the API leaves out becomes a disabled
 * row with empty values rather than a crash. The form then refuses to save
 * until somebody fills it in, which is better than inventing a number.
 */
export function toDefaultsForm(defaults) {
  const form = {};
  for (const { key } of DIRECTIONS) {
    const row = defaults?.[key] || {};
    form[key] = {
      enabled: row.enabled === true,
      weekly: toInputValue(row.weekly),
      monthly: toInputValue(row.monthly),
    };
  }
  return form;
}

/**
 * Every row is validated, enabled or not. A disabled row's values are still
 * sent and still stored, and they become live the moment somebody switches it
 * on, so they must be valid then too.
 */
export function validateDefaultsForm(form) {
  const errors = {};
  for (const { key } of DIRECTIONS) {
    const rowErrors = validateWindowPair(form?.[key] || {});
    if (Object.keys(rowErrors).length) errors[key] = rowErrors;
  }
  return errors;
}

/** Form state to the `PUT /limits/consumer-defaults` body. */
export function buildDefaultsPayload(form) {
  const payload = { currency: CURRENCY };
  for (const { key } of DIRECTIONS) {
    const row = form[key];
    payload[key] = {
      weekly: parseAmount(row.weekly),
      monthly: parseAmount(row.monthly),
      enabled: row.enabled === true,
    };
  }
  return payload;
}

/**
 * What a save would change, for the confirmation dialog. Compares parsed
 * values, so "3,750" against 3750 is not reported as a change.
 */
export function diffDefaults(before, after) {
  const changes = [];
  for (const { key, label } of DIRECTIONS) {
    const was = before?.[key] || {};
    const now = after?.[key] || {};
    if (Boolean(was.enabled) !== Boolean(now.enabled)) {
      changes.push({
        direction: key,
        label,
        field: 'enabled',
        from: was.enabled ? 'Enforced' : 'Off',
        to: now.enabled ? 'Enforced' : 'Off',
      });
    }
    for (const { key: window, label: windowLabel } of WINDOWS) {
      const from = parseAmount(was[window]);
      const to = parseAmount(now[window]);
      if (from !== to && !(Number.isNaN(from) && Number.isNaN(to))) {
        changes.push({
          direction: key,
          label,
          field: window,
          windowLabel,
          from: formatUsd(Number.isNaN(from) ? null : from),
          to: formatUsd(Number.isNaN(to) ? null : to),
        });
      }
    }
  }
  return changes;
}

/* --- One user's override ------------------------------------------------- */

/** Today's date in Kigali, as `YYYY-MM-DD` (what `<input type="date">` uses). */
export function kigaliToday(now = new Date()) {
  return new Date(now.getTime() + KIGALI_OFFSET_MS).toISOString().slice(0, 10);
}

/** The Kigali calendar date an ISO instant falls on, or '' if unparseable. */
export function kigaliDateOf(iso) {
  if (!iso) return '';
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '';
  return new Date(time + KIGALI_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * An expiry date means "through the end of that day, Kigali time". An
 * operator who picks 31 October expects the custom limits to hold on the
 * 31st, not to lapse at midnight as it begins.
 */
export function kigaliEndOfDayIso(date) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
  const instant = new Date(`${date}T23:59:59+02:00`);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

/**
 * When the current weekly window ends: the next Monday 00:00 in Kigali.
 *
 * The API reports `resets_at` itself and the screens show that. These two
 * exist for the testing-mode mock, and to pin the window rule down in a test
 * where a reader can check it against the owner's decision.
 */
export function nextWeeklyReset(now = new Date()) {
  const kigali = new Date(now.getTime() + KIGALI_OFFSET_MS);
  const day = kigali.getUTCDay(); // 0 is Sunday, 1 is Monday
  const daysToMonday = (8 - day) % 7 || 7;
  return new Date(
    Date.UTC(kigali.getUTCFullYear(), kigali.getUTCMonth(), kigali.getUTCDate() + daysToMonday) -
      KIGALI_OFFSET_MS,
  );
}

/** When the current monthly window ends: the 1st of next month, 00:00 Kigali. */
export function nextMonthlyReset(now = new Date()) {
  const kigali = new Date(now.getTime() + KIGALI_OFFSET_MS);
  return new Date(
    Date.UTC(kigali.getUTCFullYear(), kigali.getUTCMonth() + 1, 1) - KIGALI_OFFSET_MS,
  );
}

/** The effective `limit` the API reports for one direction and window. */
function currentLimit(userLimits, direction, window) {
  const row = (userLimits?.limits || []).find(
    (item) => item.direction === direction && item.window === window,
  );
  return row?.limit;
}

/**
 * API shape to the override form.
 *
 * A direction with an existing override starts ticked with those values. One
 * without starts unticked, but prefilled with whatever applies now, so that
 * ticking it starts from the user's real limit rather than from blank.
 */
export function toOverrideForm(userLimits) {
  const override = userLimits?.override || null;
  const form = {
    reason: override?.reason || '',
    expiresOn: kigaliDateOf(override?.expires_at),
  };
  for (const { key } of DIRECTIONS) {
    const custom = override?.[key] || null;
    form[key] = {
      custom: Boolean(custom),
      weekly: toInputValue(custom ? custom.weekly : currentLimit(userLimits, key, 'weekly')),
      monthly: toInputValue(custom ? custom.monthly : currentLimit(userLimits, key, 'monthly')),
    };
  }
  return form;
}

/**
 * Returns `{ deposit?, withdrawal?, p2p?, reason?, expiresOn?, form? }`.
 *
 * `isSuperAdmin` decides whether the 50,000 USD ceiling applies. `now` is
 * injectable so the expiry rule can be tested without a clock.
 */
export function validateOverrideForm(form, { isSuperAdmin = false, now = new Date() } = {}) {
  const errors = {};
  const ceiling = isSuperAdmin ? undefined : SUPER_ADMIN_OVERRIDE_CEILING_USD;

  let anyCustom = false;
  for (const { key } of DIRECTIONS) {
    if (!form?.[key]?.custom) continue;
    anyCustom = true;
    const rowErrors = validateWindowPair(form[key], { ceiling });
    if (Object.keys(rowErrors).length) errors[key] = rowErrors;
  }
  if (!anyCustom) {
    errors.form =
      'Tick at least one of deposits, withdrawals or P2P. To put the user back on the defaults, use Remove custom limits instead.';
  }

  const reason = (form?.reason || '').trim();
  if (!reason) {
    errors.reason = 'A reason is required.';
  } else if (reason.length < MIN_REASON_LENGTH) {
    const short = MIN_REASON_LENGTH - reason.length;
    errors.reason = `${short} more character${short === 1 ? '' : 's'} needed.`;
  }

  if (form?.expiresOn) {
    if (!kigaliEndOfDayIso(form.expiresOn)) {
      errors.expiresOn = 'Pick a valid date.';
    } else if (form.expiresOn < kigaliToday(now)) {
      errors.expiresOn = 'The expiry date has already passed.';
    }
  }

  return errors;
}

/** True when any value in the form is above the Super Admin ceiling. */
export function exceedsOverrideCeiling(form) {
  return DIRECTIONS.some(({ key }) => {
    const row = form?.[key];
    if (!row?.custom) return false;
    return WINDOWS.some(
      ({ key: window }) => parseAmount(row[window]) > SUPER_ADMIN_OVERRIDE_CEILING_USD,
    );
  });
}

/** Form state to the `PUT /users/{id}/limits` body. Unticked means null: use the default. */
export function buildOverridePayload(form) {
  const payload = {};
  for (const { key } of DIRECTIONS) {
    const row = form[key];
    payload[key] = row?.custom
      ? { weekly: parseAmount(row.weekly), monthly: parseAmount(row.monthly) }
      : null;
  }
  payload.reason = (form.reason || '').trim();
  payload.expires_at = form.expiresOn ? kigaliEndOfDayIso(form.expiresOn) : null;
  return payload;
}

/* --- Usage --------------------------------------------------------------- */

/**
 * The API's `limits` list grouped by direction, in display order, with each
 * group's windows in display order. Rows for a direction or window the
 * console does not know about are kept at the end rather than dropped, so a
 * new one the API adds is visible before anybody updates this file.
 */
export function groupUsage(limits = []) {
  const known = new Set(DIRECTIONS.map(({ key }) => key));
  const windowOrder = WINDOWS.map(({ key }) => key);
  const byWindow = (a, b) => {
    const ai = windowOrder.indexOf(a.window);
    const bi = windowOrder.indexOf(b.window);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  };

  const groups = DIRECTIONS.map(({ key, label }) => ({
    direction: key,
    label,
    rows: limits.filter((row) => row.direction === key).sort(byWindow),
  }));

  const extra = [...new Set(limits.map((row) => row.direction).filter((d) => !known.has(d)))];
  for (const direction of extra) {
    groups.push({
      direction,
      label: direction,
      rows: limits.filter((row) => row.direction === direction).sort(byWindow),
    });
  }
  return groups;
}

/**
 * How full one window is, for the bar. `ratio` is capped at 1 for the width;
 * `state` is what the colour and the label follow.
 */
export function usageState({ limit, used, remaining }) {
  if (limit == null || Number(limit) <= 0) {
    return { ratio: 0, state: 'unlimited' };
  }
  const ratio = Math.min(Math.max(Number(used) / Number(limit), 0), 1);
  const left = remaining != null ? Number(remaining) : Number(limit) - Number(used);
  if (left <= 0) return { ratio, state: 'at-limit' };
  if (ratio >= 0.8) return { ratio, state: 'near' };
  return { ratio, state: 'ok' };
}

/* --- Errors -------------------------------------------------------------- */

/**
 * A readable sentence from an API error.
 *
 * The API answers `{"detail": "<sentence>"}`, or a 422 whose `detail` is a
 * list. `ApiError` puts `detail` straight into `message`, so for a 422 the
 * message is the string "[object Object]" and has to be rebuilt from the list.
 */
export function describeApiError(error, fallback = 'That did not work. Nothing has been changed.') {
  const detail = error?.response?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((item) => {
        const field = (item?.loc || []).filter((part) => part !== 'body').join(' ');
        return field ? `${field}: ${item?.msg}` : item?.msg;
      })
      .filter(Boolean)
      .join(' ');
  }
  const message = error?.message;
  if (typeof message === 'string' && message && !message.startsWith('[object')) return message;
  return fallback;
}

/**
 * Field errors from a 422 list, keyed like the form: `{ deposit: { weekly } }`
 * or `{ reason }`. Anything that does not map to a field is left to
 * `describeApiError`.
 */
export function fieldErrorsFrom422(error) {
  const detail = error?.response?.detail;
  if (error?.status !== 422 || !Array.isArray(detail)) return {};
  const errors = {};
  for (const item of detail) {
    const path = (item?.loc || []).filter((part) => part !== 'body');
    const [first, second] = path;
    if (DIRECTIONS.some(({ key }) => key === first) && (second === 'weekly' || second === 'monthly')) {
      errors[first] = { ...(errors[first] || {}), [second]: item.msg };
    } else if (first === 'reason') {
      errors.reason = item.msg;
    } else if (first === 'expires_at') {
      errors.expiresOn = item.msg;
    }
  }
  return errors;
}

/**
 * A 403 the API sent because of the admin's role or the ceiling.
 *
 * The API client throws its own 403 when the CSRF token is missing from
 * memory. That one is a stale session, not a refusal, and treating it as one
 * would lock an admin who is allowed out of the form until they reloaded.
 */
export function isPermissionRefusal(error) {
  return error?.status === 403 && error?.response?.code !== 'CSRF_TOKEN_MISSING';
}
